import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import * as _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { isNumber } from '../../../models/utils';
import { Endpoint, HelpIndex, MessageType, ModalTargetType, UpdateStatusType } from '../../../models/enums';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { RestService } from '../../../services/rest/rest.service';
import { CommonService } from '../../../services/common/common.service';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Type } from '../../../models/connector';

export interface ExtractOffice365DataModalData {
  targetType: ModalTargetType;
  targets: Array<string>;
  updateStatusType?: UpdateStatusType;
  auxParsingStatus: string;
}

@Component({
  standalone: false,
  selector: 'res-extract-office-365-data-modal',
  templateUrl: './extract-office-365-data-modal.component.html',
  styleUrls: ['./extract-office-365-data-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ExtractOffice365DataModalComponent extends BaseModalComponent implements OnInit {
  @ViewChild('docRangeInfoPopover') docRangeInfoPopover: PopoverDirective;
  @ViewChild('docProcessingTimeout') docProcessingTimeout: ElementRef;

  automationId = 'extract-office-365-data';
  modalClass = 'extract-office-365-data-modal';
  modalHelpIndex = HelpIndex.extract_office_365_data_dlg;
  isBusy = false;
  isBusyMessage: string;

  extractOffice365DataModalData: ExtractOffice365DataModalData;
  documentExtractOffice365DataTimeout: number = 5;
  docRangeInfoText: string;
  docProcessingTimeoutChangedToDefaultRange = false;

  office365ExchangeConnectors: any[] = [];
  office365SharePointConnectors: any[] = [];
  selectedOffice365ConnectorHandle: string = "";

  constructor(private modalService: BsModalService, private deleteEntityRef: BsModalRef, private restService: RestService, private commonService: CommonService) {
    super(modalService, deleteEntityRef);
  }

  auxParsingStatusCleaned(): void {
    const auxParsingStatus = this.extractOffice365DataModalData.auxParsingStatus;
    this.extractOffice365DataModalData.auxParsingStatus = auxParsingStatus.toLowerCase().includes('modern attachment retrieve warning')
      ? 'modern attachment retrieve warning'
      : 'missing channel name warning';
  }

  ngOnInit(): void {
    this.extractOffice365DataModalData = this.modalData as ExtractOffice365DataModalData;
    this.auxParsingStatusCleaned();
    this.commonService.mainContent.office365Connectors.forEach(c => c.type === Type.Sharepoint ? this.office365SharePointConnectors.push(c) : this.office365ExchangeConnectors.push(c));
  }

  ok(): void {
    // If Document Processing Timeout is not within the range of 3 - 180 and 'OK' is clicked onBlur
    // the focus should be changed to the document processing timeout input field
    let fieldToFocus: HTMLElement;

    if (this.docProcessingTimeoutChangedToDefaultRange) {
      fieldToFocus = (this.docProcessingTimeout.nativeElement as HTMLElement);
    }

    if (fieldToFocus) {
      fieldToFocus.focus();
      this.docProcessingTimeoutChangedToDefaultRange = false;
      return;
    }

    this.docRangeInfoPopover.hide();

    this.isBusyMessage = 'Extracting Office 365 Data...';
    this.isBusy = true;

    const reqMsg: any = {
      connectorHandle: this.selectedOffice365ConnectorHandle,
      documentTimeout: this.documentExtractOffice365DataTimeout,
      taskDescription: 'Extracting Office 365 Data',
      auxParsingStatus: this.extractOffice365DataModalData.auxParsingStatus
    };

    let endpoint: Endpoint;
    if (this.extractOffice365DataModalData.targetType === ModalTargetType.document) {
      reqMsg.documentHandleList = this.extractOffice365DataModalData.targets;
      endpoint = Endpoint.corpusManager_extractExternalDataFromDocuments;
    } else {
      reqMsg.viewHandle = this.extractOffice365DataModalData.targets[0];
      endpoint = Endpoint.corpusManager_extractExternalDataFromView;
    }
    this.restService.post(endpoint, reqMsg, true, this.extractOffice365DataModalData.updateStatusType ? [MessageType.entityChanged, this.extractOffice365DataModalData.updateStatusType] : true).then(response => {
      this.isBusy = false;
      if (response) {
        this.closeModal();
      }
    }, () => {
      this.isBusy = false;
    });
  }

  okEnabled(): boolean {
    return this.selectedOffice365ConnectorHandle.length > 0;
  }

  restrictNumeric = (event): boolean => {
    if (event) {
      return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
    }
    return false;
  }

  showInfoPopover(modelName: string, infoText: string) {
    this.docRangeInfoPopover?.hide();
    this.docProcessingTimeoutChangedToDefaultRange = false;
    if (modelName === 'documentExtractOffice365DataTimeout') {
      this.docRangeInfoText = infoText;
      this.docRangeInfoPopover.show();
      this.docProcessingTimeoutChangedToDefaultRange = true;
    }
  }

  parseNumeric = (modelName: string, min?: number, max?: number): void => {
    if (modelName) {
      let field = _.get(this, modelName);
      if (field || (field === '')) {
        if (field === '') {
          field = 0;
        }

        let val = parseInt(field, 10);
        let text = '';
        if (isNaN(val)) {
          return;
        }
        if (isNumber(min) && min > val) {
          val = min;
          text = 'The value must be between ' + min + ' and ' + max + '. It has been changed to ' + min + '.';
          this.showInfoPopover(modelName, text);
        }
        if (isNumber(max) && val > max) {
          val = max;
          text = 'The value must be between ' + min + ' and ' + max + '. It has been changed to ' + max + '.';
          this.showInfoPopover(modelName, text);
        }
        _.set(this, modelName, val);
      }
    }
  }
}
